/**
 * Behaviour for sections/dorahi-header.liquid.
 *
 * Replaces the React state in dorahi-main's navbar / mobile-menu / search components:
 *   - hover mega menu (desktop)
 *   - slide-in drawer with accordion sub-menus (mobile)
 *   - predictive search via /search/suggest.json (desktop dropdown + mobile overlay)
 *
 * Plain custom element, no build step. Classes toggled here are safelisted in
 * tailwind.config.js so Tailwind keeps generating them.
 */
(function () {
  const SEARCH_DEBOUNCE = 300;
  const MEGA_CLOSE_DELAY = 120;

  class DorahiHeader extends HTMLElement {
    connectedCallback() {
      this.searchUrl = this.dataset.searchUrl || '/search';
      this.currencySymbol = this.dataset.currencySymbol || '';

      this.initMegaMenu();
      this.initDrawer();
      this.initSearch();
      this.trackHeaderHeight();
    }

    /**
     * Keeps --header-height / --header-group-height accurate. Horizon's own header.js
     * only measures #header-component, which this header replaces.
     */
    trackHeaderHeight() {
      const nav = this.querySelector('nav');
      const group = document.querySelector('#header-group');
      if (!nav) return;

      const measure = () => {
        document.body.style.setProperty('--header-height', `${nav.offsetHeight}px`);
        if (group) {
          let total = 0;
          for (const child of group.children) {
            if (child instanceof HTMLElement) total += child.offsetHeight;
          }
          document.body.style.setProperty('--header-group-height', `${total}px`);
        }
      };

      measure();

      if ('ResizeObserver' in window) {
        this.headerObserver = new ResizeObserver(measure);
        this.headerObserver.observe(nav);
      } else {
        window.addEventListener('resize', measure);
      }
    }

    disconnectedCallback() {
      if (this.headerObserver) this.headerObserver.disconnect();
    }

    /* ---------------------------------------------------------------- mega menu */

    initMegaMenu() {
      const triggers = this.querySelectorAll('[data-menu-trigger]');
      if (!triggers.length) return;

      this.megaTimer = null;

      const open = (key) => {
        clearTimeout(this.megaTimer);
        this.querySelectorAll('[data-mega-panel]').forEach((panel) => {
          const isActive = panel.dataset.megaPanel === key;
          panel.classList.toggle('opacity-100', isActive);
          panel.classList.toggle('translate-y-0', isActive);
          panel.classList.toggle('pointer-events-auto', isActive);
          panel.classList.toggle('opacity-0', !isActive);
          panel.classList.toggle('-translate-y-2', !isActive);
          panel.classList.toggle('pointer-events-none', !isActive);
        });
      };

      const scheduleClose = () => {
        clearTimeout(this.megaTimer);
        this.megaTimer = setTimeout(() => open(null), MEGA_CLOSE_DELAY);
      };

      triggers.forEach((trigger) => {
        const key = trigger.dataset.menuTrigger;
        trigger.addEventListener('mouseenter', () => open(key));
        trigger.addEventListener('mouseleave', scheduleClose);
      });

      this.querySelectorAll('[data-mega-panel]').forEach((panel) => {
        panel.addEventListener('mouseenter', () => open(panel.dataset.megaPanel));
        panel.addEventListener('mouseleave', scheduleClose);
        // Close as soon as a link inside is followed, matching setActiveMenu(null).
        panel.addEventListener('click', () => open(null));
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') open(null);
      });
    }

    /* ------------------------------------------------------------------ drawer */

    initDrawer() {
      const root = this.querySelector('[data-drawer-root]');
      const panel = this.querySelector('[data-drawer-panel]');
      const backdrop = this.querySelector('[data-drawer-backdrop]');
      const openButton = this.querySelector('[data-drawer-open]');
      if (!root || !panel || !backdrop || !openButton) return;

      const openDrawer = () => {
        root.classList.remove('hidden');
        openButton.setAttribute('aria-expanded', 'true');
        document.body.style.overflow = 'hidden';
        requestAnimationFrame(() => {
          backdrop.classList.remove('opacity-0');
          panel.classList.remove('-translate-x-full');
          panel.classList.add('translate-x-0');
        });
      };

      const closeDrawer = () => {
        backdrop.classList.add('opacity-0');
        panel.classList.add('-translate-x-full');
        panel.classList.remove('translate-x-0');
        openButton.setAttribute('aria-expanded', 'false');
        document.body.style.overflow = '';
        setTimeout(() => {
          root.classList.add('hidden');
          this.collapseAccordions();
        }, 300);
      };

      this.closeDrawer = closeDrawer;

      openButton.addEventListener('click', openDrawer);
      backdrop.addEventListener('click', closeDrawer);
      this.querySelectorAll('[data-drawer-close]').forEach((button) => button.addEventListener('click', closeDrawer));

      // Any link inside the drawer navigates, so close first (matches closeMobileMenu).
      panel.querySelectorAll('a').forEach((link) => link.addEventListener('click', closeDrawer));

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && !root.classList.contains('hidden')) closeDrawer();
      });

      window.addEventListener('resize', () => {
        if (window.innerWidth > 1024 && !root.classList.contains('hidden')) closeDrawer();
      });

      // Accordion: only one open at a time, same as expandedItem state.
      this.querySelectorAll('[data-accordion-toggle]').forEach((toggle) => {
        toggle.addEventListener('click', () => {
          const key = toggle.dataset.accordionToggle;
          const target = this.querySelector(`[data-accordion-panel="${key}"]`);
          const isOpen = target && !target.classList.contains('hidden');
          this.collapseAccordions();
          if (target && !isOpen) {
            target.classList.remove('hidden');
            toggle.setAttribute('aria-expanded', 'true');
            const chevron = toggle.querySelector('svg');
            if (chevron) chevron.classList.add('rotate-180');
          }
        });
      });
    }

    collapseAccordions() {
      this.querySelectorAll('[data-accordion-panel]').forEach((panel) => panel.classList.add('hidden'));
      this.querySelectorAll('[data-accordion-toggle]').forEach((toggle) => {
        toggle.setAttribute('aria-expanded', 'false');
        const chevron = toggle.querySelector('svg');
        if (chevron) chevron.classList.remove('rotate-180');
      });
    }

    /* ------------------------------------------------------------------ search */

    initSearch() {
      this.querySelectorAll('[data-search-root]').forEach((root) => this.bindSearchRoot(root));

      const overlay = this.querySelector('[data-search-overlay]');
      const openButton = this.querySelector('[data-search-open]');

      if (overlay && openButton) {
        openButton.addEventListener('click', () => {
          overlay.classList.remove('hidden');
          overlay.classList.add('flex');
          document.body.style.overflow = 'hidden';
          const input = overlay.querySelector('[data-search-input]');
          if (input) input.focus();
        });

        const closeOverlay = () => {
          overlay.classList.add('hidden');
          overlay.classList.remove('flex');
          document.body.style.overflow = '';
        };

        overlay.querySelectorAll('[data-search-close]').forEach((button) => button.addEventListener('click', closeOverlay));

        document.addEventListener('keydown', (event) => {
          if (event.key === 'Escape' && !overlay.classList.contains('hidden')) closeOverlay();
        });
      }

      // Close the desktop dropdown on outside click.
      document.addEventListener('mousedown', (event) => {
        this.querySelectorAll('[data-search-dropdown]').forEach((dropdown) => {
          const root = dropdown.closest('[data-search-root]');
          if (root && !root.contains(event.target)) dropdown.classList.add('hidden');
        });
      });
    }

    bindSearchRoot(root) {
      const input = root.querySelector('[data-search-input]');
      const form = root.querySelector('form');
      if (!input || !form) return;

      const dropdown = root.querySelector('[data-search-dropdown]');
      const trending = root.querySelector('[data-search-trending]');
      const results = root.querySelector('[data-search-results]');
      const viewAll = root.querySelector('[data-search-viewall]');
      const spinner = root.querySelector('[data-search-spinner]');
      const magnifier = root.querySelector('[data-search-magnifier]');
      const clearButton = root.querySelector('[data-search-clear]');

      let timer = null;
      let controller = null;

      const setLoading = (loading) => {
        if (spinner) spinner.classList.toggle('hidden', !loading);
        if (magnifier) magnifier.classList.toggle('hidden', loading);
      };

      const showTrending = () => {
        if (trending) trending.classList.remove('hidden');
        if (results) {
          results.classList.add('hidden');
          results.innerHTML = '';
        }
        if (viewAll) viewAll.classList.add('hidden');
      };

      const run = async (query) => {
        if (controller) controller.abort();
        controller = new AbortController();
        setLoading(true);

        try {
          const url =
            '/search/suggest.json?q=' +
            encodeURIComponent(query) +
            '&resources[type]=product,query&resources[limit]=6&resources[options][unavailable_products]=last';
          const response = await fetch(url, { signal: controller.signal });
          const data = await response.json();
          const resources = (data.resources && data.resources.results) || {};

          if (trending) trending.classList.add('hidden');
          if (results) {
            results.innerHTML = this.renderResults(resources, query);
            results.classList.remove('hidden');
          }
          if (viewAll) viewAll.classList.remove('hidden');
        } catch (error) {
          if (error.name !== 'AbortError') {
            console.warn('[dorahi-header] predictive search failed:', error);
            if (results) {
              results.innerHTML =
                '<div class="p-8 text-center text-gray-500 text-[14px]">Search is unavailable right now.</div>';
              results.classList.remove('hidden');
            }
          }
        } finally {
          setLoading(false);
        }
      };

      input.addEventListener('input', () => {
        const query = input.value.trim();
        if (clearButton) clearButton.classList.toggle('hidden', query.length === 0);
        clearTimeout(timer);

        if (query.length < 2) {
          showTrending();
          return;
        }

        timer = setTimeout(() => run(query), SEARCH_DEBOUNCE);
      });

      input.addEventListener('focus', () => {
        if (dropdown) dropdown.classList.remove('hidden');
      });

      if (clearButton) {
        clearButton.addEventListener('click', () => {
          input.value = '';
          clearButton.classList.add('hidden');
          showTrending();
          input.focus();
        });
      }

      root.querySelectorAll('[data-search-submit]').forEach((button) => {
        button.addEventListener('click', () => form.submit());
      });
    }

    renderResults(resources, query) {
      const products = resources.products || [];
      const queries = resources.queries || [];
      let html = '';

      if (queries.length) {
        html += '<div class="p-4 border-b border-gray-300">';
        html += '<h3 class="text-[12px] font-bold text-gray-400 uppercase mb-2 tracking-wider">Suggestions</h3>';
        html += '<div class="space-y-2">';
        queries.forEach((item) => {
          html +=
            '<a href="' +
            this.searchUrl +
            '?q=' +
            encodeURIComponent(item.text) +
            '" class="block w-full text-left text-[14px] text-text-main hover:text-dorahi-pink transition-colors">' +
            (item.styled_text || this.escape(item.text)) +
            '</a>';
        });
        html += '</div></div>';
      }

      if (products.length) {
        html += '<div class="p-4">';
        html += '<h3 class="text-[12px] font-bold text-gray-400 uppercase mb-2 tracking-wider">Products</h3>';
        html += '<div class="space-y-3">';
        products.forEach((product) => {
          const image = product.featured_image && product.featured_image.url ? product.featured_image.url : product.image;
          html +=
            '<a href="' +
            product.url +
            '" class="flex items-center gap-3 group">' +
            '<span class="w-[72px] h-20 block shrink-0 rounded-[20px] rounded-squircle overflow-hidden bg-gray-50">' +
            (image
              ? '<img src="' +
                image +
                '" alt="' +
                this.escape(product.title) +
                '" loading="lazy" class="w-full h-full object-cover object-top">'
              : '') +
            '</span>' +
            '<span class="flex-1 min-w-0 block">' +
            '<span class="block text-[14px] text-text-main font-medium truncate group-hover:text-dorahi-pink transition-colors">' +
            this.escape(product.title) +
            '</span>' +
            '<span class="block text-[12px] text-gray-500 font-bold">' +
            this.formatPrice(product.price) +
            '</span>' +
            '</span></a>';
        });
        html += '</div></div>';
      }

      if (!products.length && !queries.length) {
        html =
          '<div class="p-8 text-center text-gray-500 text-[14px]">No results found for &quot;' +
          this.escape(query) +
          '&quot;</div>';
      }

      return html;
    }

    /**
     * suggest.json returns the price as a decimal string in the shop currency.
     * The Next.js design shows a rounded amount after the symbol, so match that.
     */
    formatPrice(price) {
      if (price === undefined || price === null || price === '') return '';
      const amount = Number(String(price).replace(/[^0-9.]/g, ''));
      if (Number.isNaN(amount)) return '';
      return this.currencySymbol + amount.toFixed(0);
    }

    escape(value) {
      const div = document.createElement('div');
      div.textContent = value == null ? '' : String(value);
      return div.innerHTML;
    }

  }

  if (!customElements.get('dorahi-header')) {
    customElements.define('dorahi-header', DorahiHeader);
  }
})();
